import React, { useState, useEffect } from 'react';
import CreateMarket from './CreateMarket';
import Portfolio from './Portfolio.js';
import TradePage from './components/Trade/TradePage';
import ResolvePage from './components/Resolve/ResolvePage';
import ClaimPage from './components/Claim/ClaimPage';
import AdminSettlementPage from './components/Admin/AdminSettlementPage';
import AdminPage from './pages/AdminPage';
import DisputesPage from './pages/DisputesPage';
import MarketsPage from './pages/MarketsPage';
import { securityMasterLoader } from './data/securityMaster';
import { useAuth } from './hooks/useAuth';
import Header from './components/Header';
import AuthModal from './components/AuthModal';
import Dashboard from './components/Dashboard/Dashboard';
import ProfilePage from './components/Profile/ProfilePage';
import './App.css';

const TABS_PUBLIC = [
    { id: 'markets',    label: '📊 Live Markets' },
    { id: 'trade',      label: '💰 Trade' },
    { id: 'disputes',   label: '⚠️ Disputes' },
] as const;

const TABS_AUTH = [
    { id: 'markets',            label: '📊 Live Markets' },
    { id: 'dashboard',          label: '📈 Dashboard' },
    { id: 'trade',              label: '💰 Trade' },
    { id: 'claim',              label: '🏆 Claim' },
    { id: 'create',             label: '➕ Create Market' },
    { id: 'resolve',            label: '⚖️ Resolve' },
    { id: 'admin-settlement',   label: '🛡️ Settlement' },
    { id: 'admin',              label: '⚙️ Admin' },
    { id: 'disputes',           label: '⚠️ Disputes' },
    { id: 'portfolio',          label: '💼 Portfolio' },
    { id: 'profile',            label: '👤 Profile' },
] as const;

type TabId =
    | 'markets'
    | 'dashboard'
    | 'trade'
    | 'claim'
    | 'create'
    | 'resolve'
    | 'admin-settlement'
    | 'admin'
    | 'disputes'
    | 'portfolio'
    | 'profile';

type AuthView = 'login' | 'signup' | null;

const App: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabId>('markets');
    const [authModal, setAuthModal] = useState<AuthView>(null);

    const { isAuthenticated, user, logout } = useAuth();

    // Kick off background loading of all 80 000+ securities on app startup.
    // init() is idempotent – safe to call multiple times.
    useEffect(() => {
        securityMasterLoader.init().catch((err) => {
            // Errors are surfaced through per-category status; log for debugging.
            console.debug('[SecurityMaster] init error:', err);
        });
    }, []);

    // When user logs out, redirect to markets tab
    useEffect(() => {
        if (!isAuthenticated && ['dashboard', 'profile', 'claim', 'create', 'resolve', 'admin-settlement', 'admin', 'portfolio'].includes(activeTab)) {
            setActiveTab('markets');
        }
    }, [isAuthenticated, activeTab]);

    const tabs = isAuthenticated ? TABS_AUTH : TABS_PUBLIC;

    return (
        <div className="app">
            {/* Header */}
            <Header
                user={user}
                isAuthenticated={isAuthenticated}
                onSignIn={() => setAuthModal('login')}
                onSignUp={() => setAuthModal('signup')}
                onProfile={() => setActiveTab('profile')}
                onLogout={logout}
            />

            {/* Navigation */}
            <nav className="navigation" aria-label="Main navigation">
                <ul>
                    {tabs.map(tab => (
                        <li key={tab.id}>
                            <button
                                className={`nav-tab${activeTab === tab.id ? ' nav-tab-active' : ''}`}
                                onClick={() => setActiveTab(tab.id as TabId)}
                                aria-current={activeTab === tab.id ? 'page' : undefined}
                            >
                                {tab.label}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>

            {/* Main Content */}
            <main className="main-content">
                {activeTab === 'markets'   && <MarketsPage user={user} />}
                {activeTab === 'trade'     && <TradePage user={user} />}
                {activeTab === 'claim'     && isAuthenticated && <ClaimPage user={user} />}
                {activeTab === 'create'    && isAuthenticated && <CreateMarket user={user} />}
                {activeTab === 'resolve'           && isAuthenticated && <ResolvePage user={user} />}
                {activeTab === 'admin-settlement'  && isAuthenticated && <AdminSettlementPage user={user} />}
                {activeTab === 'admin'             && isAuthenticated && <AdminPage user={user} />}
                {activeTab === 'disputes'          && <DisputesPage />}
                {activeTab === 'portfolio'         && isAuthenticated && <Portfolio />}
                {activeTab === 'dashboard' && isAuthenticated && user && (
                    <Dashboard user={user} />
                )}
                {activeTab === 'profile'   && isAuthenticated && user && (
                    <ProfilePage user={user} />
                )}
            </main>

            <footer className="app-footer">
                <p>© 2025 SmartPredictions · Powered by Ethereum &amp; CoinGecko</p>
            </footer>

            {/* Auth Modal */}
            {authModal && (
                <AuthModal
                    initialView={authModal}
                    onClose={() => setAuthModal(null)}
                />
            )}
        </div>
    );
};

export default App;
